import React from "react";
import { Button, Icon, LinearProgress, Typography } from "@material-ui/core";
import GroupPanel from "./GroupPanel";
import SubGroupPanel from "./SubGroupPanel";
import StepperDetailCourse from "../StepperDetailCourse";
import { useStyles } from "./styles";
import { Translator } from "@itarget/shared";
import { enumsSchedule } from "@itarget/shared/src/enums";

const ProgramingOnDemandGrouped = ({
  groups = {},
  selectStep,
  defaultModule,
  openQuestionFromCurrentLessonWithQuestionId,
  onOpenSubmodule,
  finalResultsByScheduleId,
  isLoadingFinalResult = false,
  settingCommerce,
  isLoadingDetail,
}) => {
  const classes = useStyles();
  const [expandedGroup, setExpandedGroup] = React.useState(
    Object.entries(groups).length === 1
      ? Object.keys(groups)[0]
      : defaultModule || null
  );
  const [expandedSubGroups, setExpandedSubGroups] = React.useState({});
  const formatTimeString = (totalTime) => {
    if (!totalTime || totalTime === "00:00:00") return "";
    const [h, m, s] = totalTime.split(":").map(Number);
    return `${h}h ${m}min`;
  };

  const translation = {
    watchAgain: Translator({ transRef: "translation.Rever vídeo" }),
    watch: Translator({ transRef: "translation.Assistir vídeo" }),
    unavailable: Translator({ transRef: "translation.Indisponível" }),
    yes: Translator({ transRef: "translation.Sim" }).props.children,
    no: Translator({ transRef: "translation.Não" }).props.children,
    attemptsProceed: Translator({
      transRef:
        "translation.Atenção: Você possui (%s) tentativas restantes. Deseja prosseguir?",
    }).props.children,
    attemptProceed: Translator({
      transRef:
        "translation.Atenção: Você possui (%s) tentativa restante. Deseja prosseguir?",
    }).props.children,
    notAnswered: Translator({ transRef: "translation.Não respondido" }).props
      .children,
    questionnary: Translator({ transRef: "translation.Questionário" }).props
      .children,
    questionnaires: Translator({ transRef: "translation.Questionários" }).props
      .children,
    notDefined: Translator({ transRef: "translation.Não definido" }).props
      .children,
    showAnswers: Translator({ transRef: "translation.Resultados" }).props
      .children,
    files: Translator({ transRef: "translation.Arquivos" }).props.children,
    showContent: Translator({ transRef: "translation.Ver aulas" }).props
      .children,
    hideContent: Translator({ transRef: "translation.Ocultar aulas" }).props
      .children,
  };

  return (
    <div className={classes.root}>
      {Object.entries(groups).map(([groupName, groupData]) => {
        const { total_tempo, total_registros } = groupData;
        const timeString = formatTimeString(total_tempo);
        const infoParts = [
          `${Object.keys(groupData.groups || {}).length} tópicos`,
          `${total_registros} aulas`,
          timeString,
        ]
          .filter(Boolean)
          .join(" • ");
        let colorHelper = 0;

        return (
          <GroupPanel
            key={groupName}
            accordionProps={{
              className: classes.groupHeaderContainer,
              expanded: expandedGroup === groupName,
              onChange: (_, isExpanded) =>
                setExpandedGroup(isExpanded ? groupName : null),
            }}
            title={
              <div className={classes.groupPanelTitleContainer}>
                <Typography className={classes.groupHeader}>
                  {groupName}
                </Typography>
                {infoParts && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.groupHeaderInfo}
                  >
                    {infoParts}
                  </Typography>
                )}
              </div>
            }
          >
            {Object.entries(groupData.groups || {}).map(
              ([subGroupName, subGroupData]) => {
                const {
                  total_tempo: subTotalTime,
                  total_registros: subTotalRegistros,
                } = subGroupData;
                const subTimeString = formatTimeString(subTotalTime);
                const subInfoParts = [
                  `${subTotalRegistros} aulas`,
                  subTimeString,
                ]
                  .filter(Boolean)
                  .join(" • ");
                colorHelper++;
                const date = subGroupData.data_atividade
                  ? new Date(`${subGroupData.data_atividade}T00:00:00`)
                  : null;
                const day = date?.getDate();
                const month = date?.toLocaleDateString("pt-BR", {
                  month: "short",
                });

                const subModuleQuizz = finalResultsByScheduleId
                  ? finalResultsByScheduleId[
                      subGroupData?.agenda_atividade_detalhe_pai_id
                    ] || finalResultsByScheduleId[
                      subGroupData?.agenda_atividade_detalhe_id
                    ] || []
                  : [];

                const alreadyWatched = subGroupData?.total_assistido >= 99;
                const watchedSomething = subGroupData?.total_assistido > 0;

                const showWatchButton = () => {
                  const scheduleValidityMode = subGroupData?.modo_vigencia;
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  if (
                    scheduleValidityMode ===
                      enumsSchedule.SCHEDULE_VALIDITY_MODE.NOT_APPLICABLE ||
                    scheduleValidityMode === undefined
                  )
                    return true;

                  if (
                    scheduleValidityMode ===
                      enumsSchedule.SCHEDULE_VALIDITY_MODE.FIXED_DATE &&
                    new Date(`${subGroupData?.data_vigencia_fixa}T00:00:00`) >=
                      today
                  )
                    return true;

                  if (
                    scheduleValidityMode ===
                      enumsSchedule.SCHEDULE_VALIDITY_MODE
                        .ACCORDING_TO_THE_INSCRIPTION &&
                    new Date(
                      `${subGroupData?.data_vigencia_associacao}T00:00:00`
                    ) >= today
                  )
                    return true;

                  return false;
                };

                const handleExpand = () => {
                  const isExpanded =
                    expandedSubGroups[groupName] === subGroupName;
                  
                  if (!isExpanded && typeof onOpenSubmodule === "function") {
                    // Pega a primeira apresentação como exemplo
                    const anySession = Object.values(
                      subGroupData.presentations || {}
                    )[0];
                    
                    // Obtém os IDs corretos, priorizando os da subGroupData
                    const parentId = subGroupData.agenda_atividade_detalhe_pai_id || 
                                   anySession?.agenda_atividade_detalhe_pai_id;
                                   
                    const detailId = subGroupData.agenda_atividade_detalhe_id || 
                                    anySession?.agenda_atividade_detalhe_id;
                    
                    // Chama onOpenSubmodule apenas se tivermos IDs válidos
                    if (onOpenSubmodule && (parentId || detailId)) {
                      onOpenSubmodule(
                        parentId,
                        detailId
                      );
                    }
                  }

                  // Toggle do estado de expansão
                  setExpandedSubGroups((prev) => ({
                    ...prev,
                    [groupName]: !isExpanded ? subGroupName : null,
                  }));
                };

                return (
                  <div className={classes.SubGroupPanelContainer}>
                    {day && month && (
                      <div
                        onClick={handleExpand}
                        data-parity={colorHelper % 2 === 0 ? "even" : "odd"}
                        className={classes.dateInformation}
                      >
                        <Typography className={classes.day}>{day}</Typography>
                        <Typography className={classes.month}>
                          {month}
                        </Typography>
                      </div>
                    )}
                    <SubGroupPanel
                      key={subGroupName}
                      selectStep={selectStep}
                      sessionsArray={subGroupData.presentations}
                      accordionProps={{
                        hasExpandIcon: !(day && month),
                        className: classes.subGroupHeaderContainer,
                        expanded: expandedSubGroups[groupName] === subGroupName,
                        onChange: (_, isExpanded) => {
                          return false;
                        },
                      }}
                      title={
                        <Typography onClick={handleExpand} variant="subtitle1">
                          {subGroupName}
                        </Typography>
                      }
                      infoString={subInfoParts}
                      conditions={
                        <div className={classes.conditionContainer}>
                          {subGroupData?.link_video_palestrante && (
                            <>
                              {showWatchButton() ? (
                                <Button
                                  endIcon={
                                    alreadyWatched ? (
                                      <Icon>replay</Icon>
                                    ) : (
                                      <Icon>play_arrow</Icon>
                                    )
                                  }
                                  variant="text"
                                  disabled={isLoadingDetail}
                                  className={classes.watchButton}
                                  data-watched-something={watchedSomething}
                                  onClick={() =>
                                    selectStep(
                                      {
                                        ...subGroupData,
                                        titulo_palestra: subGroupName,
                                      },
                                      subGroupData?.palestrantes_nome,
                                      "video"
                                    )
                                  }
                                >
                                  <Typography className={classes.buttonLabel}>
                                    {alreadyWatched
                                      ? translation.watchAgain
                                      : translation.watch}
                                  </Typography>
                                </Button>
                              ) : (
                                <Button
                                  endIcon={
                                    <Icon className={classes.buttonIcon}>
                                      block
                                    </Icon>
                                  }
                                  className={`btn-watch ${classes.unavailableButton}`}
                                >
                                  <Typography className={classes.buttonLabel}>
                                    {translation.unavailable}
                                  </Typography>
                                </Button>
                              )}
                            </>
                          )}
                          <Button
                            endIcon={
                              <Icon className={classes.buttonIcon}>
                                subscriptions
                              </Icon>
                            }
                            onClick={handleExpand}
                            className={`btn-watch ${classes.accordionButton}`}
                          >
                            <Typography className={classes.buttonLabel}>
                              {expandedSubGroups[groupName] === subGroupName
                                ? translation.hideContent
                                : translation.showContent}
                            </Typography>
                          </Button>
                        </div>
                      }
                    >
                      {(isLoadingFinalResult || isLoadingDetail) && (
                        <LinearProgress />
                      )}
                      {subModuleQuizz.length > 0 && (
                        <StepperDetailCourse
                          steps={[
                            {
                              ...subGroupData,
                              titulo_palestra: subGroupName,
                            },
                          ]}
                          selectStep={selectStep}
                          openQuestionFromCurrentLessonWithQuestionId={
                            openQuestionFromCurrentLessonWithQuestionId
                          }
                          finalResults={subModuleQuizz}
                          isLoadingFinalResult={isLoadingFinalResult}
                          settingCommerce={settingCommerce}
                          isLoadingDetail={isLoadingDetail}
                          subModule={true}
                        />
                      )}
                      {Object.entries(subGroupData.presentations || {}).map(
                        ([sessionTitle, sessionData]) => (
                          <div key={sessionTitle}>
                            <StepperDetailCourse
                              steps={[
                                {
                                  ...sessionData,
                                  titulo_palestra: sessionTitle,
                                },
                              ]}
                              selectStep={selectStep}
                              openQuestionFromCurrentLessonWithQuestionId={
                                openQuestionFromCurrentLessonWithQuestionId
                              }
                              finalResults={
                                finalResultsByScheduleId
                                  ? finalResultsByScheduleId[
                                      sessionData.agenda_atividade_detalhe_pai_id ||
                                        sessionData.agenda_atividade_detalhe_id
                                    ] || []
                                  : []
                              }
                              isLoadingFinalResult={isLoadingFinalResult}
                              settingCommerce={settingCommerce}
                              isLoadingDetail={isLoadingDetail}
                            />
                          </div>
                        )
                      )}
                    </SubGroupPanel>
                  </div>
                );
              }
            )}
          </GroupPanel>
        );
      })}
    </div>
  );
};

export default ProgramingOnDemandGrouped;
